import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, Close, DeleteOutline, ExpandMore, UploadFile } from '@mui/icons-material';
import { FileTree } from '../files/FileTree';
import { useAssistantAdminContext } from '../../contexts/AssistantAdminContext';
import { buildFilesUploadUrl, postFilesFormData } from '../../utils/fileUpload';
import { AssistantDefinition, DocumentAnswerPromptDefinition } from '../../client/SmartAssistantBackendClient';

export type AssistantEditDialogProps = {
  open: boolean;
  adminAssistantEditIndex: number | null;
  onClose: () => void;
};

export function AssistantEditDialog({
  open,
  adminAssistantEditIndex,
  onClose,
}: AssistantEditDialogProps) {
  const { state, actions, files, uploads, consumer } = useAssistantAdminContext();
  const {
    documentAssistantDrafts,
    assistantUploadState,
    existingAssistantInternalNames,
    assistantSaveState,
  } = state;
  const {
    saveAssistant,
    deleteAssistant,
  } = actions;
  const {
    listFilesForAssistant,
    embedFileForAssistant,
    deleteFileForAssistant,
    renameFolderForAssistant,
    deleteFolderForAssistant,
    uploadFilesToFolderForAssistant,
  } = files;
  const { updateAssistantUploadState } = uploads;

  const [draft, setDraft] = useState<AssistantDefinition | null>(null);

  const buildDraft = useCallback((assistant?: AssistantDefinition | null) => (
    new AssistantDefinition({
      id: assistant?.id,
      name: assistant?.name ?? '',
      internalName: assistant?.internalName ?? '',
      topics: assistant?.topics ?? '',
      vectorStoreId: assistant?.vectorStoreId ?? '',
      isDefault: assistant?.isDefault ?? false,
      assistentPrompt: assistant?.assistentPrompt ?? '',
      assistentLlmModel: assistant?.assistentLlmModel ?? '',
      promptDefinitions: (assistant?.promptDefinitions ?? []).map((prompt) => new DocumentAnswerPromptDefinition({
        key: prompt.key ?? '',
        displayName: prompt.displayName ?? '',
        promptInstruction: prompt.promptInstruction ?? '',
        llmModel: prompt.llmModel ?? '',
        isDefault: prompt.isDefault ?? false,
      })),
    })
  ), []);

  useEffect(() => {
    if (adminAssistantEditIndex === null) {
      setDraft(null);
      return;
    }
    const source = documentAssistantDrafts[adminAssistantEditIndex];
    if (!source) {
      setDraft(null);
      return;
    }
    setDraft(buildDraft(source));
  }, [adminAssistantEditIndex, buildDraft, documentAssistantDrafts]);

  const originalInternalName = useMemo(() => {
    if (adminAssistantEditIndex === null) return '';
    return documentAssistantDrafts[adminAssistantEditIndex]?.internalName?.trim() ?? '';
  }, [adminAssistantEditIndex, documentAssistantDrafts]);

  const updateDraft = useCallback((patch: Partial<AssistantDefinition>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return new AssistantDefinition({
        id: prev.id,
        name: patch.name ?? prev.name,
        internalName: patch.internalName ?? prev.internalName,
        topics: patch.topics ?? prev.topics,
        vectorStoreId: patch.vectorStoreId ?? prev.vectorStoreId,
        isDefault: patch.isDefault ?? prev.isDefault,
        assistentPrompt: patch.assistentPrompt ?? prev.assistentPrompt,
        assistentLlmModel: patch.assistentLlmModel ?? prev.assistentLlmModel,
        promptDefinitions: patch.promptDefinitions ?? prev.promptDefinitions,
      });
    });
  }, []);

  const updatePrompt = useCallback((promptIndex: number, patch: Partial<DocumentAnswerPromptDefinition>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const currentPrompts = prev.promptDefinitions ?? [];
      const nextPrompts = currentPrompts.map((prompt, pIdx) => {
        if (pIdx !== promptIndex) return prompt;
        return new DocumentAnswerPromptDefinition({
          key: patch.key ?? prompt.key,
          displayName: patch.displayName ?? prompt.displayName,
          promptInstruction: patch.promptInstruction ?? prompt.promptInstruction,
          llmModel: patch.llmModel ?? prompt.llmModel,
          isDefault: patch.isDefault ?? prompt.isDefault,
        });
      });
      return new AssistantDefinition({
        ...prev,
        promptDefinitions: nextPrompts,
      });
    });
  }, []);

  const addPrompt = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const currentPrompts = prev.promptDefinitions ?? [];
      const nextPrompts = [
        ...currentPrompts,
        new DocumentAnswerPromptDefinition({
          key: '',
          displayName: '',
          promptInstruction: '',
          llmModel: '',
          isDefault: false,
        }),
      ];
      return new AssistantDefinition({
        ...prev,
        promptDefinitions: nextPrompts,
      });
    });
  }, []);

  const removePrompt = useCallback((promptIndex: number) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Prompt wirklich löschen?');
      if (!confirmed) return;
    }
    setDraft((prev) => {
      if (!prev) return prev;
      const currentPrompts = prev.promptDefinitions ?? [];
      const nextPrompts = currentPrompts.filter((_, pIdx) => pIdx !== promptIndex);
      return new AssistantDefinition({
        ...prev,
        promptDefinitions: nextPrompts,
      });
    });
  }, []);

  const setPromptDefault = useCallback((promptIndex: number, next: boolean) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const currentPrompts = prev.promptDefinitions ?? [];
      const nextPrompts = currentPrompts.map((prompt, pIdx) => new DocumentAnswerPromptDefinition({
        key: prompt.key,
        displayName: prompt.displayName,
        promptInstruction: prompt.promptInstruction,
        llmModel: prompt.llmModel,
        isDefault: pIdx === promptIndex ? next : (next ? false : prompt.isDefault),
      }));
      return new AssistantDefinition({
        ...prev,
        promptDefinitions: nextPrompts,
      });
    });
  }, []);

  const handleEmbedUploadForAssistant = async (assistantInternalName: string) => {
    const current = assistantUploadState[assistantInternalName];
    if (!current?.file) {
      updateAssistantUploadState(assistantInternalName, { message: 'Keine Datei ausgewählt.' });
      return;
    }
    updateAssistantUploadState(assistantInternalName, { status: 'uploading', message: 'Upload & Einbetten läuft …' });
    try {
      const token = await consumer.getUserToken();
      const uploadUrl = buildFilesUploadUrl({ endpoint: 'upload', subfolder: 'uploads', assistantInternalName });
      await postFilesFormData(uploadUrl, [current.file], token as string);
      updateAssistantUploadState(assistantInternalName, { status: 'done', message: 'Erfolgreich hochgeladen und eingebettet.', file: null });
    } catch (err: any) {
      console.error(err);
      updateAssistantUploadState(assistantInternalName, { status: 'error', message: err?.message ?? 'Fehler beim Hochladen oder Einbetten' });
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: (theme) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }),
      }}
    >
      {adminAssistantEditIndex !== null && draft && (() => {
        const assistant = draft;
        const internalName = assistant.internalName?.trim() ?? '';
        const nameLabel = assistant.name?.trim() || internalName || `Assistent ${adminAssistantEditIndex + 1}`;
        const assistantKey = assistant.id ?? `draft-${adminAssistantEditIndex}`;
        const uploadStateKey = assistantKey;
        const uploadState = assistantUploadState[uploadStateKey] ?? { file: null, status: 'idle', message: null };
        const hasInternalName = Boolean(internalName);
        const isInternalNameLocked = hasInternalName
          && existingAssistantInternalNames.has(internalName)
          && internalName !== originalInternalName;
        const isInternalNameReadonly = Boolean(assistant.id) || isInternalNameLocked;
        const saveState = assistantSaveState[assistantKey] ?? { status: 'idle', message: null };

        return (
          <>
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {nameLabel}
              </Typography>
              <IconButton onClick={onClose} sx={{ color: 'text.secondary' }} aria-label="Schließen">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2 }}>
              <Box display="grid" gap={1.5}>
                <TextField
                  label="ID"
                  value={assistant.id ?? ''}
                  disabled
                />
                <TextField
                  label="Name"
                  value={assistant.name ?? ''}
                  onChange={(ev) => updateDraft({ name: ev.target.value })}
                />
                <TextField
                  label="Internal Name"
                  value={assistant.internalName ?? ''}
                  helperText={isInternalNameReadonly
                    ? 'Internal Name ist fix und kann nicht geändert werden.'
                    : 'Wird für URLs und Dateipfade verwendet.'}
                  disabled={isInternalNameReadonly}
                  onChange={(ev) => updateDraft({ internalName: ev.target.value })}
                />
                <TextField
                  label="Themen / Beschreibung"
                  value={assistant.topics ?? ''}
                  onChange={(ev) => updateDraft({ topics: ev.target.value })}
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Assistenten-Prompt"
                  value={assistant.assistentPrompt ?? ''}
                  onChange={(ev) => updateDraft({ assistentPrompt: ev.target.value })}
                  multiline
                  minRows={4}
                  maxRows={12}
                />
                <TextField
                  label="Assistent LLM"
                  value={assistant.assistentLlmModel ?? ''}
                  onChange={(ev) => updateDraft({ assistentLlmModel: ev.target.value })}
                />
                <TextField
                  label="Vector Store ID"
                  value={assistant.vectorStoreId ?? ''}
                  helperText="Vorhandenen OpenAI Vector Store ID eintragen, um einen bestehenden Store zuzuweisen."
                  onChange={(ev) => updateDraft({ vectorStoreId: ev.target.value })}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(assistant.isDefault)}
                      onChange={(ev) => updateDraft({ isDefault: ev.target.checked })}
                    />
                  )}
                  label="Als Standardassistent verwenden"
                />
              </Box>

              <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Prompt-Konfigurationen
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={addPrompt}
                >
                  Prompt hinzufügen
                </Button>
              </Box>

              {(assistant.promptDefinitions?.length ?? 0) === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Prompts vorhanden.
                </Typography>
              ) : (
                <Box display="grid" gap={1.5}>
                  {(assistant.promptDefinitions ?? []).map((prompt, promptIndex) => (
                    <Accordion
                      key={`${assistantKey}-prompt-${promptIndex}`}
                      defaultExpanded={!prompt.key && !prompt.displayName && !prompt.promptInstruction}
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.default,
                        '&:before': { display: 'none' },
                      })}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
                      >
                        <Typography variant="subtitle2">
                          {prompt.displayName?.trim()
                            || prompt.key?.trim()
                            || `Prompt ${promptIndex + 1}`}
                        </Typography>
                        {prompt.isDefault && (
                          <Chip label="Standard" size="small" color="primary" />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ display: 'grid', gap: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            startIcon={<DeleteOutline />}
                            onClick={() => removePrompt(promptIndex)}
                          >
                            {`Prompt löschen (${prompt.displayName?.trim()
                              || prompt.key?.trim()
                              || `Prompt ${promptIndex + 1}`})`}
                          </Button>
                        </Box>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={Boolean(prompt.isDefault)}
                              onChange={(ev) => setPromptDefault(promptIndex, ev.target.checked)}
                            />
                          )}
                          label="Als Standard-Prompt verwenden"
                        />
                        <TextField
                          label="Key"
                          value={prompt.key ?? ''}
                          onChange={(ev) => updatePrompt(promptIndex, { key: ev.target.value })}
                        />
                        <TextField
                          label="Anzeigename"
                          value={prompt.displayName ?? ''}
                          onChange={(ev) => updatePrompt(promptIndex, { displayName: ev.target.value })}
                        />
                        <TextField
                          label="LLM Model"
                          value={prompt.llmModel ?? ''}
                          onChange={(ev) => updatePrompt(promptIndex, { llmModel: ev.target.value })}
                          helperText={`Leer = Assistent LLM (${(assistant.assistentLlmModel ?? '').trim() || 'Backend Default'})`}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {`Effektiv: ${(prompt.llmModel ?? '').trim()
                            || (assistant.assistentLlmModel ?? '').trim()
                            || 'Backend Default'}`}
                        </Typography>
                        <TextField
                          label="Prompt Instruction"
                          value={prompt.promptInstruction ?? ''}
                          onChange={(ev) => updatePrompt(promptIndex, { promptInstruction: ev.target.value })}
                          fullWidth
                          multiline
                          minRows={6}
                          maxRows={16}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}

              <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

              <Typography variant="subtitle2" color="text.secondary">
                Dateien & Ordner
              </Typography>
              {hasInternalName ? (
                <FileTree
                  rootKey={assistantKey}
                  loadChildren={(path) => listFilesForAssistant(internalName, path)}
                  onEmbed={(item) => embedFileForAssistant(internalName, item)}
                  onDelete={(item) => deleteFileForAssistant(internalName, item)}
                  onRenameFolder={(sourcePath, targetPath) => renameFolderForAssistant(internalName, sourcePath, targetPath)}
                  onDeleteFolder={(folderPath) => deleteFolderForAssistant(internalName, folderPath)}
                  onUploadToFolder={(folderPath, files) => uploadFilesToFolderForAssistant(internalName, folderPath, files)}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Bitte zuerst einen Internal Name setzen, um Dateien zu verwalten.
                </Typography>
              )}

              <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

              <Typography variant="subtitle2" color="text.secondary">
                Datei hochladen & einbetten
              </Typography>
              {hasInternalName ? (
                <Box display="grid" gap={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button variant="outlined" component="label" startIcon={<UploadFile />} fullWidth>
                      Datei auswählen
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            updateAssistantUploadState(uploadStateKey, { file: e.target.files[0], status: 'idle', message: null });
                          }
                        }}
                      />
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!uploadState.file || uploadState.status === 'uploading' || uploadState.status === 'embedding'}
                      onClick={() => handleEmbedUploadForAssistant(internalName)}
                      fullWidth
                    >
                      {uploadState.status === 'uploading'
                        ? 'Upload läuft …'
                        : uploadState.status === 'embedding'
                          ? 'Einbetten …'
                          : 'Hochladen & einbetten'}
                    </Button>
                  </Stack>

                  {uploadState.file && (
                    <Typography variant="body2" color="text.secondary">
                      {uploadState.file.name}
                    </Typography>
                  )}

                  {uploadState.message && (
                    <Typography variant="caption" color={uploadState.status === 'error' ? 'error' : 'text.secondary'}>
                      {uploadState.message}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Bitte zuerst einen Internal Name setzen.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => saveAssistant(draft, adminAssistantEditIndex)}
                disabled={saveState.status === 'saving' || saveState.status === 'deleting'}
              >
                {saveState.status === 'saving' ? 'Speichern …' : 'Assistent speichern'}
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<DeleteOutline />}
                onClick={() => {
                  deleteAssistant(adminAssistantEditIndex);
                  onClose();
                }}
                disabled={saveState.status === 'saving' || saveState.status === 'deleting'}
              >
                {saveState.status === 'deleting'
                  ? `Assistent löschen … (${nameLabel})`
                  : `Assistent löschen (${nameLabel})`}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={onClose}
              >
                Schließen
              </Button>
              <Box flexGrow={1} />
              {saveState.message && (
                <Typography
                  variant="caption"
                  color={saveState.status === 'error' ? 'error' : 'text.secondary'}
                >
                  {saveState.message}
                </Typography>
              )}
            </DialogActions>
          </>
        );
      })()}
    </Dialog>
  );
}
